import React, { useState } from 'react'
import { Message } from 'primereact/message'
import { Divider } from 'primereact/divider'
import { useOrderData } from '../../../hooks/useOrderData'
import { useOrderSelection } from '../../../hooks/useOrderSelection'
import { useLineItems } from '../../../hooks/useLineItems'
import { useLineItemSelection } from '../../../hooks/useLineItemSelection'
import { RejectedItemsKPIs } from './shared/RejectedItemsKPIs'
import { RejectedOrderCard } from './shared/RejectedOrderCard'
import { ViewOrderInfoDialog } from '../ViewOrderInfoDialog'
import { ShippingOrder } from '../../../DeShippingTableWrapper'

interface OrderAnalysis {
  storeDeclinedOnly: number
  adminRejectedOnly: number
  mixedOrders: number
  totalOrders: number
}

interface RejectedItemsAnalysisViewProps {
  orderData: ReturnType<typeof useOrderData>
  orderSelection: ReturnType<typeof useOrderSelection>
  lineItemsHook: ReturnType<typeof useLineItems>
  lineItemSelection: ReturnType<typeof useLineItemSelection>
  orderAnalysis: OrderAnalysis | null
}

export const RejectedItemsAnalysisView: React.FC<RejectedItemsAnalysisViewProps> = ({
  orderData,
  orderSelection,
  lineItemsHook,
  lineItemSelection,
  orderAnalysis
}) => {
  const [isOrderDialogVisible, setIsOrderDialogVisible] = useState(false)

  if (!orderAnalysis) return null

  // Handler for viewing order details when card is clicked
  const handleOrderCardClick = (order: ShippingOrder) => {
    orderSelection.setSelectedOrder(order)
    setIsOrderDialogVisible(true)
  }

  const selectedOrders = orderSelection.getSelectedOrdersData()

  return (
    <div>
      {/* Summary KPIs Section */}
      <RejectedItemsKPIs 
        selectedOrders={selectedOrders}
        orderAnalysis={orderAnalysis}
      />

      <Divider />

      {/* Orders List Section */}
      <div className="mb-4">
        <h6 className="mb-3 flex align-items-center gap-2">
          <i className="pi pi-list text-primary" style={{ fontSize: '1.1rem', marginRight: '8px' }}></i>
          Orders with Rejected Items ({selectedOrders.length})
        </h6>
        
        <div className="orders-container" style={{ maxHeight: '300px', overflowY: 'auto' }}>
          {selectedOrders.map((order) => (
            <RejectedOrderCard 
              key={order.order_id} 
              order={order} 
              onOrderClick={handleOrderCardClick}
            />
          ))}
        </div>
      </div>

      {/* Info Message for Unified Reassignment */}
      <Message
        severity="info"
        content={
          <div className="flex align-items-center gap-2">
            <i className="pi pi-info-circle" style={{ marginRight: '0.5rem' }}></i>
            <span>
              All rejected items from these orders will be reassigned to a different store location. 
              This includes both store-declined and admin-rejected items.
              Click on any order card to view detailed information.
            </span>
          </div>
        }
        className="mb-4"
      />

      {/* ViewOrderInfoDialog */}
      <ViewOrderInfoDialog
        orderSelection={orderSelection}
        lineItemsHook={lineItemsHook}
        lineItemSelection={lineItemSelection}
        visible={isOrderDialogVisible}
        onHide={() => setIsOrderDialogVisible(false)}
      />
    </div>
  )
} 